import './CarouselList.scss';

import { Typography } from '@modulor/react';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import type { FC } from 'react';
import type { UIPaymentGroups, UIPayoutGroups } from 'store/formatters';
import { useAppSelector } from 'store/hooks';
import {
  carouselMethodListSelector,
  isCarouselInitLoadSelector,
  isCarouselViewChangedSelector,
} from 'store/slices/carousel';
import { paymentFlowSelector } from 'store/slices/global';
import { isOnFormPageSelector } from 'store/slices/methodForm';
import { groupingCarouselMethodsSelector, isShowGroupingMethodsInCarouselSelector } from 'store/slices/methodGrouping';
import { isSubmitFormActiveSelector, submitRedirectTypeRedirectSelector } from 'store/slices/submitForm';

import { isDepositFlowType } from '../../utils';
import { CarouselList } from './CarouselList';

interface CarouselControllerProps {
  onMethodSelect?: (methodId: number) => void;
  preloadedMethods?: (UIPaymentGroups | UIPayoutGroups)[];
}

export const CarouselController: FC<CarouselControllerProps> = ({ onMethodSelect, preloadedMethods }) => {
  const isCarouselInitLoad = useAppSelector(isCarouselInitLoadSelector);
  const isCarouselViewChanged = useAppSelector(isCarouselViewChangedSelector);
  const legacyList = useAppSelector(carouselMethodListSelector);
  const groupingList = useAppSelector(groupingCarouselMethodsSelector);
  const isShowGroupingMethodsInCarousel = useAppSelector(isShowGroupingMethodsInCarouselSelector);
  const isOnFormPage = useAppSelector(isOnFormPageSelector);
  const isSubmitFormActive = useAppSelector(isSubmitFormActiveSelector);
  const isRedirectTypeRedirect = useAppSelector(submitRedirectTypeRedirectSelector);
  const { skeletonT } = useSkeletonTranslation();
  const flowType = useAppSelector(paymentFlowSelector);
  const captionMessageKey = isDepositFlowType(flowType)
    ? 'PH.NAVIGATION.PAYMENT_METHODS.TITLE'
    : 'PH.NAVIGATION.PAYOUT_METHODS.TITLE';

  // Grouping mode: use derived selector (no dispatch needed)
  // Legacy mode: use carousel.methodList from store
  const list = isShowGroupingMethodsInCarousel ? groupingList : legacyList;
  const minLength = isShowGroupingMethodsInCarousel ? 1 : 2;

  const methodsToShow = list.length >= minLength ? list : preloadedMethods || [];

  if (
    !isCarouselInitLoad ||
    methodsToShow.length < minLength ||
    isRedirectTypeRedirect ||
    isSubmitFormActive ||
    (isShowGroupingMethodsInCarousel && !isOnFormPage) ||
    (!isShowGroupingMethodsInCarousel && isCarouselViewChanged)
  ) {
    return null;
  }

  return (
    <section className="carousel" data-testid="carousel">
      {!isShowGroupingMethodsInCarousel && (
        <div className="carousel-methods-label">
          <Typography variant="body-regular">{skeletonT(captionMessageKey)}</Typography>
        </div>
      )}
      <CarouselList flowType={flowType} list={methodsToShow} onMethodSelect={onMethodSelect} />
    </section>
  );
};
